import React from 'react';
import {
  BasicContainer,
  BodyTextLarge,
  BodyTextSmall,
  FlexCol,
  FlexRow,
  H1Text,
  H3Text,
  PaywallButton,
} from '@minecraft.atoms';
import IconWithErrorBoundary from '@minecraft.icon';
import { ArtistSubscriptionFragment } from '@minecraft.graphql-operations';
import { gqlSubscription } from '@minecraft.graphql-types';
import { BillingCodes } from '../../enums/plans';
import { priceFormatter } from '../../helpers/priceFormatter';
import { Trans, useTranslation } from '../../../I18N';
import { PlanType } from '../../usePlans';

type Props = {
  plans?: PlanType[];
  subscription?: ArtistSubscriptionFragment | gqlSubscription;
  onCTAClick: () => void;
};

export const ExpiredCardlessTrialModal = ({ plans, subscription, onCTAClick }: Props) => {
  const { t } = useTranslation();
  const plan = plans?.find((planFromNetwork) => planFromNetwork.billingCode === BillingCodes.premiumMonth);

  const now = new Date();
  const target = new Date(subscription?.trialEndsAt);
  const diff = Math.max(target.getTime() - now.getTime(), 0);
  const trialDaysLeft = Math.floor(diff / (1000 * 60 * 60 * 24));

  const price = priceFormatter({
    price: Number(plan?.price),
    currencyCode: plan?.currencyCode,
    t,
    roundingFloor: true,
  });

  const planFeatures = [
    'common:paywall.features.premium.unlimitedSubmissionsAndMedia',
    'common:paywall.features.premium.talentScout',
    'common:paywall.features.premium.trackEverySubmissions',
    'common:paywall.features.premium.connectAllSocials',
  ];

  return (
    <FlexCol justifyItems="center" alignItems="center" gap="6" w="full" mt="8" mb="4">
      <IconWithErrorBoundary name="warning-banner" />
      <H1Text fontWeight="semibold" as="span" textWrap="balance" align="center" color="error">
        {t('common:paywall.trial.expired.titleModal', { trialDaysLeft })}
      </H1Text>
      <BodyTextSmall>{t('common:paywall.plan.activeCardlessTrial.getFullAccessTagline')}</BodyTextSmall>
      <FlexCol w="full" justifyItems="center" alignItems="center" gap="2">
        <BodyTextLarge fontWeight="medium" as="span" textWrap="balance" align="center">
          <Trans
            i18nKey="common:paywall.plan.upgrade_premium"
            components={[<H3Text fontWeight="bold" as="span" />]}
            values={{
              price,
            }}
          />
        </BodyTextLarge>
        <BodyTextSmall color="disabled">{t('common:paywall.plan.billed_monthly')}</BodyTextSmall>
      </FlexCol>
      <FlexCol w="full" gap="4" mdProps={{ w: '2/3' }}>
        {planFeatures?.map((feature) => (
          <FlexRow gap="3" alignItems="start" w="full" key={feature}>
            <IconWithErrorBoundary name="check-circle" themeColor="discountOffer" />
            <BasicContainer w="4/5">
              <BodyTextSmall textWrap="balance">
                <Trans i18nKey={feature} components={[<b />]} />
              </BodyTextSmall>
            </BasicContainer>
          </FlexRow>
        ))}
        <PaywallButton
          label={t('common:paywall.plan.expiredCardlessTrial.unlock')}
          color="discount"
          onClick={onCTAClick}
          full
          isSmall
        />
      </FlexCol>
    </FlexCol>
  );
};
